// What the list screens remember between visits to a detail page: the list's
// last address (with its filters, search, sorting and page) and how far down
// it was scrolled. It lives in sessionStorage, so it is per tab and survives a
// reload; when storage is unavailable (private modes, tests) a plain Map does
// the job for as long as the page stays open.
const memory = new Map();

/** @param {string} key @returns {string|null} */
const read = (key) => {
    try {
        return sessionStorage.getItem(key);
    } catch {
        return memory.get(key) ?? null; // storage blocked
    }
};

/**
 * @param {string} key
 * @param {string} value
 */
const write = (key, value) => {
    try {
        sessionStorage.setItem(key, value);
    } catch {
        memory.set(key, value); // storage blocked or full
    }
};

/**
 * Remember where a list was last seen.
 * @param {string} basePath the list's own path, e.g. "/orders"
 * @param {string} url the full address, e.g. "/orders?status=PA&page=2"
 */
export const rememberListUrl = (basePath, url) => write(`ordo:list:${basePath}`, url);

/**
 * Where to send someone who wants to go back to a list: the address it was
 * last seen at, or the plain list if it wasn't seen yet.
 * @param {string} basePath
 */
export const recallListUrl = (basePath) => {
    const url = read(`ordo:list:${basePath}`);
    // only ever the list itself, whatever sessionStorage holds
    return url && (url === basePath || url.startsWith(`${basePath}?`)) ? url : basePath;
};

/**
 * The address a list was last seen at, or null if it never was. (Unlike
 * `recallListUrl`, this tells "never opened" apart from "opened with its
 * defaults", which both lead to the plain path.)
 * @param {string} basePath
 */
export const recallListUrlIfSeen = (basePath) => {
    const url = read(`ordo:list:${basePath}`);
    return url && (url === basePath || url.startsWith(`${basePath}?`)) ? url : null;
};

/**
 * @param {string} url a list address, query string included
 * @param {number} y pixels scrolled
 */
export const rememberScroll = (url, y) => write(`ordo:scroll:${url}`, String(Math.round(y)));

/** @param {string} url @returns {number} pixels scrolled, 0 if unknown */
export const recallScroll = (url) => {
    const y = Number(read(`ordo:scroll:${url}`));
    return Number.isFinite(y) && y > 0 ? y : 0;
};

/**
 * The navigation that goes back to a list: its last address, flagged so the
 * list scrolls back to where it was.
 * @param {string} basePath
 * @returns {{to: string, state: {restoreScroll: true}}}
 */
export const listTarget = (basePath) => ({to: recallListUrl(basePath), state: {restoreScroll: true}});
